import { get, writable, type Writable } from 'svelte/store';

export function useSlottedElements({ element: bindedElement = writable(null) }: { element?: Writable<HTMLElement | null> } = {}) {
	/**
	 * Variables
	 */

	const element: Writable<HTMLElement | null> = writable(null);

	/**
	 * Retrieves the elements assigned to the slot inside the given component.
	 *
	 * @returns {Element[]} An array of assigned elements, or an empty array if no slot is found.
	 */
	const getSlottedElements = (name = 'default'): Element[] => {
		let slots = getSlots(name);

		if (slots && slots.length) {
			const slottedElements = new Array();

			slots.forEach((item) => {
				let slot = item as HTMLSlotElement;
				slottedElements.push(...slot.assignedElements());
			});

			return slottedElements;
		} else {
			return [];
		}
	};

	/**
	 * Retrieves the nodes assigned to the slot inside the given component.
	 *
	 * @returns {Element[]} An array of assigned nodes, or an empty array if no slot is found.
	 */
	const getSlottedNodes = (name = 'default'): Element[] => {
		let slots = getSlots(name);

		if (slots && slots.length) {
			const slottedElements = new Array();

			slots.forEach((item) => {
				let slot = item as HTMLSlotElement;
				slottedElements.push(...slot.assignedNodes());
			});

			return slottedElements;
		} else {
			return [];
		}
	};

	const getSiblingSlots = (): { previous: HTMLSlotElement | null; next: HTMLSlotElement | null } => {
		const el = get(bindedElement) ?? get(element);

		let previous: HTMLSlotElement | null = null;
		let next: HTMLSlotElement | null = null;

		const _prevElement = el?.previousElementSibling;
		if (_prevElement?.nodeName === 'SLOT') {
			const slot = _prevElement as HTMLSlotElement;
			previous = slot;
		}

		const _nextElement = el?.nextElementSibling;
		if (_nextElement?.nodeName === 'SLOT') {
			const slot = _nextElement as HTMLSlotElement;
			next = slot;
		}

		return {
			previous,
			next,
		};
	};

	/**
	 *
	 * @param name
	 * @returns
	 */
	const getSlots = (name = 'default'): NodeListOf<Element> | HTMLSlotElement[] | null => {
		const el = get(bindedElement) ?? get(element);

		if (!el) return null;

		let slots;
		if (name === 'default') {
			slots = el.querySelectorAll('slot:not([name])');
		} else if (name === '*') {
			const siblingsSlots = getSiblingSlots();
			const mainSlots = el.querySelectorAll('slot');
			slots = [
				...(siblingsSlots.previous ? [siblingsSlots.previous] : []),
				...mainSlots,
				...(siblingsSlots.next ? [siblingsSlots.next] : []),
			];
		} else {
			slots = el.querySelectorAll(`slot[name="${name}"]`);
		}

		return slots;
	};

	/**
	 * Iterates over all slotted elements and applies the provided callback function to each.
	 *
	 * @param _callback - A function to be executed for each slotted element, receiving the element and its index.
	 *                    Defaults to an empty function if no callback is provided.
	 */

	const forEachSlottedElement = (_callback: (element: Element, index: number) => void = () => {}, slotName = 'default'): void => {
		const slottedElements = getSlottedElements(slotName);
		slottedElements.forEach(_callback);
	};

	/**
	 * Iterates over all slotted nodes and applies the provided callback function to each.
	 *
	 * @param _callback - A function to be executed for each slotted node, receiving the node and its index.
	 *                    Defaults to an empty function if no callback is provided.
	 */

	const forEachSlottedNode = (_callback: (element: Element, index: number) => void = () => {}, slotName = 'default'): void => {
		const slottedElements = getSlottedNodes(slotName);
		slottedElements.forEach(_callback);
	};

	return {
		element,
		getSlottedElements,
		getSlottedNodes,
		forEachSlottedElement,
		forEachSlottedNode,
	};
}
